import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, ArrowsClockwise } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import StripeCheckoutForm from "@/components/StripeCheckoutForm";
import { API } from "@/config";

const formatMoney = (cents, currency) => {
  try {
    return new Intl.NumberFormat("en-NZ", { style: "currency", currency: (currency || "nzd").toUpperCase() }).format(
      Math.abs(cents) / 100
    );
  } catch {
    return `$${(Math.abs(cents) / 100).toFixed(2)}`;
  }
};

const PLANS = [
  { key: "small", name: "Small", price: "$50", limit: "Up to 30 members" },
  { key: "medium", name: "Medium", price: "$75", limit: "Up to 60 members" },
  { key: "large", name: "Large", price: "$90", limit: "Unlimited members" },
];

const STATUS_LABELS = {
  trialing: "Trial",
  active: "Active",
  past_due: "Payment overdue",
  canceled: "Canceled",
};

const BillingPage = () => {
  const { org } = useAuth();
  const [searchParams] = useSearchParams();
  const [orgDetails, setOrgDetails] = useState(org);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [previewingPlan, setPreviewingPlan] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null); // { planKey, total, amount_due, currency }
  const [confirming, setConfirming] = useState(false);
  const [checkoutPlan, setCheckoutPlan] = useState(null); // plan key while the payment dialog is open
  const [publishableKey, setPublishableKey] = useState(null);

  const fetchOrg = async () => {
    try {
      const { data } = await axios.get(`${API}/organizations/me`);
      setOrgDetails(data);
    } catch (error) {
      console.error("Error fetching organization:", error);
    }
  };

  useEffect(() => {
    fetchOrg();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const checkout = searchParams.get("checkout");
    if (checkout === "success") {
      toast.success("Payment received! Activating your plan...");
      // The webhook lands within a second or two of the redirect — give it
      // a moment, then refresh so the new plan shows up.
      const t = setTimeout(fetchOrg, 2000);
      return () => clearTimeout(t);
    } else if (checkout === "canceled") {
      toast.info("Checkout canceled — no charge was made.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Opens Stripe's embedded payment form in a dialog. The publishable key is
  // served by the backend so it always matches the backend's secret key mode.
  const handleUpgrade = async (planKey) => {
    setLoadingPlan(planKey);
    try {
      let key = publishableKey;
      if (!key) {
        const { data } = await axios.get(`${API}/billing/config`);
        key = data.publishable_key;
        setPublishableKey(key);
      }
      setCheckoutPlan(planKey);
    } catch (error) {
      console.error("Error loading billing config:", error);
      toast.error(error.response?.data?.detail || "Failed to start checkout");
    } finally {
      setLoadingPlan(null);
    }
  };

  const handleRequestPlanChange = async (planKey) => {
    setPreviewingPlan(planKey);
    try {
      const { data } = await axios.post(`${API}/billing/preview-plan-change`, { plan: planKey });
      setConfirmDialog({ planKey, ...data });
    } catch (error) {
      console.error("Error previewing plan change:", error);
      toast.error(error.response?.data?.detail || "Failed to preview this plan change");
    } finally {
      setPreviewingPlan(null);
    }
  };

  const handleConfirmPlanChange = async () => {
    if (!confirmDialog) return;
    setConfirming(true);
    try {
      await axios.post(`${API}/billing/change-plan`, { plan: confirmDialog.planKey });
      toast.success("Plan updated.");
      setConfirmDialog(null);
      await fetchOrg();
    } catch (error) {
      console.error("Error changing plan:", error);
      toast.error(error.response?.data?.detail || "Failed to change plan");
    } finally {
      setConfirming(false);
    }
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const { data } = await axios.post(`${API}/billing/create-portal-session`);
      window.location.href = data.portal_url;
    } catch (error) {
      console.error("Error creating portal session:", error);
      toast.error(error.response?.data?.detail || "Failed to open billing portal");
      setPortalLoading(false);
    }
  };

  if (!orgDetails) {
    return (
      <div className="p-4 sm:p-6 lg:p-12">
        <div className="text-zinc-500 text-sm">Loading...</div>
      </div>
    );
  }

  const isTrial = orgDetails.plan === "trial";
  const hasSubscription = !!orgDetails.stripe_subscription_id;
  const isPaid = !isTrial;
  const currentPlanInfo = PLANS.find((p) => p.key === orgDetails.plan);

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-4xl">
      <h1 className="text-2xl font-semibold text-zinc-900 mb-1">Billing</h1>
      <p className="text-sm text-zinc-500 mb-8">Manage your Club Pass subscription</p>

      <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-6 mb-8">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Current Plan</p>
            <p className="text-xl font-semibold text-zinc-900">
              {isTrial ? "Free Trial" : currentPlanInfo?.name || orgDetails.plan}
            </p>
            <p className="text-sm text-zinc-500 mt-1">
              {STATUS_LABELS[orgDetails.status] || orgDetails.status}
              {isPaid && currentPlanInfo ? ` — ${currentPlanInfo.price}/year` : ""}
            </p>
          </div>
          {isPaid && orgDetails.stripe_customer_id && (
            <Button variant="outline" onClick={handleManageBilling} disabled={portalLoading} data-testid="manage-billing-btn">
              {portalLoading ? (
                <ArrowsClockwise size={16} className="mr-2 animate-spin" />
              ) : null}
              Manage Billing
            </Button>
          )}
        </div>

        <div className="mt-5 pt-5 border-t border-zinc-200 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-zinc-500 mb-1">Active Members</p>
            <p className="text-sm font-medium text-zinc-900">
              {orgDetails.member_count}
              {orgDetails.member_limit != null ? ` / ${orgDetails.member_limit}` : " (unlimited)"}
            </p>
          </div>
          {isTrial && (
            <div>
              <p className="text-xs text-zinc-500 mb-1">Trial Passes Issued</p>
              <p className="text-sm font-medium text-zinc-900">
                {orgDetails.trial_passes_issued} / {orgDetails.trial_passes_limit}
              </p>
            </div>
          )}
        </div>
      </div>

      {isTrial && (
        <p className="text-sm text-zinc-600 mb-4">
          You're on the free trial — design your pass and issue up to {orgDetails.trial_passes_limit} test
          passes (photos are watermarked). Subscribe to a plan to remove the watermark and the pass limit.
        </p>
      )}
      {hasSubscription && (
        <p className="text-sm text-zinc-600 mb-4">
          Need more room, or less? Switch plans below — you'll be charged or credited the prorated
          difference right away, and it applies immediately (no need to cancel first).
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {PLANS.map((plan) => {
          const isCurrent = hasSubscription && orgDetails.plan === plan.key;
          return (
            <div key={plan.key} className="border border-zinc-200 rounded-xl p-6 flex flex-col" data-testid={`plan-card-${plan.key}`}>
              <p className="text-lg font-semibold text-zinc-900">{plan.name}</p>
              <p className="text-2xl font-bold text-zinc-900 mt-2">
                {plan.price}<span className="text-sm font-normal text-zinc-500">/year</span>
              </p>
              <p className="text-sm text-zinc-500 mt-2 mb-6 flex items-center gap-1.5">
                <CheckCircle size={16} className="text-[#16A34A]" weight="fill" />
                {plan.limit}
              </p>
              {isCurrent ? (
                <Button className="mt-auto" variant="outline" disabled data-testid={`current-plan-btn-${plan.key}`}>
                  Current Plan
                </Button>
              ) : hasSubscription ? (
                <Button
                  className="mt-auto bg-[var(--brand)] hover:bg-[var(--brand-dark)] text-white"
                  onClick={() => handleRequestPlanChange(plan.key)}
                  disabled={previewingPlan !== null}
                  data-testid={`change-plan-btn-${plan.key}`}
                >
                    {previewingPlan === plan.key ? "Checking price..." : "Switch to this plan"}
                  </Button>
                ) : (
                  <Button
                    className="mt-auto bg-[var(--brand)] hover:bg-[var(--brand-dark)] text-white"
                    onClick={() => handleUpgrade(plan.key)}
                    disabled={loadingPlan !== null}
                    data-testid={`upgrade-btn-${plan.key}`}
                  >
                    {loadingPlan === plan.key ? "Loading..." : "Subscribe"}
                  </Button>
                )}
              </div>
            );
          })}
      </div>

      <Dialog open={!!checkoutPlan} onOpenChange={(open) => !open && setCheckoutPlan(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="checkout-dialog">
          <DialogHeader>
            <DialogTitle>
              Subscribe to {PLANS.find((p) => p.key === checkoutPlan)?.name} —{" "}
              {PLANS.find((p) => p.key === checkoutPlan)?.price}/year
            </DialogTitle>
          </DialogHeader>
          {checkoutPlan && publishableKey && (
            <StripeCheckoutForm planKey={checkoutPlan} publishableKey={publishableKey} />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDialog} onOpenChange={(open) => !open && setConfirmDialog(null)}>
        <DialogContent className="max-w-md" data-testid="confirm-plan-change-dialog">
          <DialogHeader>
            <DialogTitle>
              Switch to {PLANS.find((p) => p.key === confirmDialog?.planKey)?.name}?
            </DialogTitle>
          </DialogHeader>
          {confirmDialog && (
            <>
              <p className="text-sm text-zinc-600 mt-1">
                This takes effect immediately — no need to cancel your current plan first.
              </p>
              <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-4 mt-3 text-center">
                {confirmDialog.amount_due > 0 ? (
                  <>
                    <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">You'll be charged today</p>
                    <p className="text-2xl font-bold text-zinc-900">
                      {formatMoney(confirmDialog.amount_due, confirmDialog.currency)}
                    </p>
                  </>
                ) : confirmDialog.total < 0 ? (
                  <>
                    <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">
                      No charge today — you'll get a credit of
                    </p>
                    <p className="text-2xl font-bold text-zinc-900">
                      {formatMoney(confirmDialog.total, confirmDialog.currency)}
                    </p>
                  </>
                ) : (
                  <p className="text-sm font-medium text-zinc-900">No charge due right now.</p>
                )}
              </div>
              <div className="flex gap-3 mt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setConfirmDialog(null)}
                  disabled={confirming}
                  data-testid="cancel-plan-change-btn"
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-[var(--brand)] hover:bg-[var(--brand-dark)] text-white"
                  onClick={handleConfirmPlanChange}
                  disabled={confirming}
                  data-testid="confirm-plan-change-btn"
                >
                  {confirming ? "Switching..." : "Confirm & Switch"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BillingPage;
